export type C100Timeframe = '1D' | '3D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y'

// The tabs the table offers. `1D` stays in the union — the API still serves it
// — but it is no longer a tab: the Day column already carries today's move, so
// every tab here is a multi-session comparison against it.
export const C100_TIMEFRAMES: C100Timeframe[] = [
  '3D',
  '5D',
  '1M'
  // Temporarily disabled — re-enable when ready:
  // '6M',
  // 'YTD',
  // '1Y',
  // '5Y'
]

export interface C100Mover {
  ticker: string
  name: string
  pct: number
}

export interface C100Banner {
  nameCount: number
  sectorCount: number
  avgMovePct: number
  lead: C100Mover | null
  laggard: C100Mover | null
}

export type ObOsState = 'OB' | 'OS' | 'N'

// Screen signals frozen at publish. All nullable — 0 is a real score, so a
// missing value arrives as null and renders as an em dash, never a fake zero.
export interface C100Signals {
  score: number | null // composite 0-100
  screenCount: number // how many screens the name hit; 1 shows no chip
  marketCap: number | null
  tech: number | null // Technical
  fund: number | null // Fundamental
  mom: number | null // Momentum
  obOs: { st: ObOsState; it: ObOsState; lt: ObOsState } | null
  upStreakDays: number | null // consecutive up days
  trendLabel: string | null // e.g. 'Established Uptrend'
}

// One ranked slot exactly as published: everything frozen into the version.
export interface C100Entry extends C100Signals {
  ticker: string
  rank: number
  rankDelta: number | null // vs previous version; positive = climbed, null = new/first list
  isNew: boolean
  name: string
  sector: string
  industry: string
  market: string
}

export interface C100Row extends C100Entry {
  price: number
  dayChangePct: number
  timeframeChangePct: number | null
  trend: 'up' | 'down' | 'flat' // timeframe move direction — distinct from trendLabel
}

export interface C100Payload {
  version: number
  updatedAt: string
  timeframe: C100Timeframe
  banner: C100Banner
  rows: C100Row[]
}

// ── History: published lists, read back frozen. Dates are ISO strings. ──

export interface C100HistorySummary {
  version: number
  publishedAt: string
  isCurrent: boolean
  tickerCount: number
  addedCount: number | null // null on the first published list
  droppedCount: number | null
}

export interface C100HistoryChanges {
  added: { ticker: string; name: string; rank: number }[]
  dropped: { ticker: string; name: string; lastRank: number }[]
  movers: { ticker: string; name: string; rank: number; rankDelta: number }[]
}

export interface C100VersionRef {
  version: number
  publishedAt: string
}

export interface C100HistoryVersion {
  version: number
  publishedAt: string
  isCurrent: boolean
  previous: C100VersionRef | null
  next: C100VersionRef | null
  entries: C100Entry[]
  changes: C100HistoryChanges | null // null on the first published list
}

export interface C100TimelinePoint {
  version: number
  publishedAt: string
  rank: number | null // null = off the list that week
  score: number | null
  trendLabel: string | null
}

export interface C100TickerTimeline {
  ticker: string
  name: string
  firstAddedAt: string
  lastSeenAt: string
  onListNow: boolean
  weeksOnList: number
  points: C100TimelinePoint[] // oldest first, from first appearance onward
}
